import React, { useEffect } from "react";
import cx from "classnames";
import { AnimatePresence, motion } from "motion/react";
import { Icon } from "@foris/avocado-icons";
import { Heading, RoundButton, Select, SelectPagination, Tag } from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import { getRestrictionOperators, getRestrictionValue } from "@utils/restrictions";
import { EnrollmentRestrictionOperator } from "@models/ISchema";
import { emitEvent, Events, useListenEvent } from "@common/hooks/useListenEvent";
import useGetHeaderInfo from "@dimensions/group/hooks/useGetHeaderInfo";
import GroupInheritedRestrictions from "@dimensions/group/group-inherited-restrictions";
import useGroupRestrictionsValidations from "@dimensions/group/hooks/useGroupRestrictionsValidations";
import useGroupRestrictionsValues from "@dimensions/group/hooks/useGroupRestrictionsValues";
import useValueSelectorFactory from "@dimensions/group/hooks/useValueSelectorFactory";
import useUpdateGroupRestrictions from "@dimensions/group/hooks/useUpdateGroupRestrictions";
import GroupRestrictionsSkeleton from "./GroupRestrictionsSkeleton";
import styles from "./groupsRestrictions.module.scss";

export interface GroupRestrictionsProps {
  scenario: string;
  origin: string;
  id: string;
  onSuccess?: () => void;
}

const GroupRestrictions = ({ scenario, origin, id, onSuccess }: GroupRestrictionsProps) => {
  const { t } = useTranslation();
  const { header, loading, error, calendarData } = useGetHeaderInfo({
    externalId: id,
  });

  const {
    groupsRestrictionProperties,
    groupRestrictions,
    groupRestrictionsByKey,
    inheritedSubjectRestrictions,
    inheritedSubjectRestrictionsByKey,
    selectedRestrictionsByPropertyKey,
    groupEnrollmentRestrictionsHasChanged,
    isEmpty,
    handleAddGroupRestrictionValue,
    handleRemoveGroupRestrictionValue,
    handleUpdateGroupRestrictionOperator,
    handleRemoveGroupRestriction,
  } = useGroupRestrictionsValues();

  const { selectedProperty, selectedOperator, setSelectedProperty, setSelectedOperator } =
    useGroupRestrictionsValidations({
      inheritedSubjectRestrictionsByKey,
      groupRestrictionsByKey,
    });

  const { valueSelectorFactory } = useValueSelectorFactory({
    propertyType: selectedProperty?.value,
    scenario,
    origin,
    source: selectedProperty?.self?.source ?? null,
    customModelCode: selectedProperty?.self?.source_filter ?? null,
    ignoreAccessFilters: selectedProperty?.self?.ignore_access_filters,
  });

  const { handleUpdateGroupRestrictions, isLoading: isUpdatingGroupRestrictions } =
    useUpdateGroupRestrictions({
      onSuccess,
    });

  const showSkeleton = (loading || !calendarData || !header || isEmpty) && !error;

  useEffect(() => {
    emitEvent("on:update", {
      target: "group-restrictions-changes",
      data: {
        hasChanges:
          groupEnrollmentRestrictionsHasChanged && !showSkeleton && !isUpdatingGroupRestrictions,
      },
    });
  }, [groupEnrollmentRestrictionsHasChanged]);

  useEffect(() => {
    emitEvent("on:update", {
      target: "group-restrictions-saving",
      data: {
        isSaving: isUpdatingGroupRestrictions,
      },
    });

    return () => {
      emitEvent("on:update", {
        target: "group-restrictions-saving",
        data: {
          isSaving: false,
        },
      });
    };
  }, [isUpdatingGroupRestrictions]);

  const handleSaveChanges = async () => {
    if (isUpdatingGroupRestrictions || showSkeleton) return;

    const restrictionsPayload = groupRestrictions?.map(restriction => {
      return {
        attribute: restriction?.self?.attribute,
        operator: restriction?.self?.operator,
        table: restriction?.self?.table,
        values: restriction?.self?.values?.map(value => {
          if (value?.__typename === "CustomModelValue" && (value as any)?.self?.value) {
            return (value as any)?.self?.value;
          }

          return getRestrictionValue(value)?.code as string;
        }),
      };
    });

    handleUpdateGroupRestrictions({
      scenarioId: scenario,
      originId: origin,
      groupId: id,
      restrictions: restrictionsPayload,
    });
  };

  useListenEvent({
    eventKey: "on:submit",
    handler: (detail: Events["on:submit"]) => {
      if (detail?.target === "group-restrictions-save-changes") {
        handleSaveChanges();
      }
    },
  });

  if (showSkeleton) {
    return <GroupRestrictionsSkeleton />;
  }

  return (
    <div className={styles.groupRestrictions}>
      {inheritedSubjectRestrictions?.length > 0 && (
        <GroupInheritedRestrictions restrictions={inheritedSubjectRestrictions} />
      )}

      <div className={styles.groupRestrictions_form}>
        <div className={styles.formHeader}>
          <Icon name="edit" color="neutral-50" />
          <Heading type="h3">{t("group-restrictions.manage-restrictions.form.title")}</Heading>
        </div>

        <div className={styles.formSelectors}>
          <Select
            className={styles.formSelectors_select}
            label={t("group-restrictions.manage-restrictions.form.property")}
            placeholder={t("group-restrictions.manage-restrictions.form.property-placeholder")}
            options={groupsRestrictionProperties}
            value={selectedProperty}
            onChange={setSelectedProperty}
          />
          <Select
            className={styles.formSelectors_select}
            label={t("group-restrictions.manage-restrictions.form.condition")}
            placeholder={t("group-restrictions.manage-restrictions.form.condition-placeholder")}
            options={getRestrictionOperators()}
            value={selectedOperator}
            onChange={option => {
              setSelectedOperator(option);
              handleUpdateGroupRestrictionOperator({
                attribute: selectedProperty?.value,
                operator: option?.value as EnrollmentRestrictionOperator,
              });
            }}
            disabled={!selectedProperty || selectedOperator?.isInherited}
          />

          <SelectPagination
            key={`value-selector-${selectedProperty?.value}`}
            className={styles.formSelectors_select}
            label={t("group-restrictions.manage-restrictions.form.value")}
            placeholder={t("group-restrictions.manage-restrictions.form.value-placeholder")}
            loadOptions={valueSelectorFactory?.loadOptions}
            value={null}
            disabled={!selectedOperator?.value || !valueSelectorFactory}
            isOptionDisabled={(option: any) => {
              const isValueSaved = selectedRestrictionsByPropertyKey[
                selectedProperty?.value
              ]?.valueIds?.includes(+option?.self?.id);

              return isValueSaved;
            }}
            onChange={(option: any) => {
              handleAddGroupRestrictionValue({
                attribute: selectedProperty?.value,
                operator: selectedOperator?.value as EnrollmentRestrictionOperator,
                table: selectedProperty?.self?.source_key_raw,
                value: option?.self,
              });
            }}
          />
        </div>

        <div className={styles.formCards}>
          {groupRestrictions?.map(restriction => {
            const ogOperatorLabel = restriction?.ogOperatorLabel;
            const operatorLabel = restriction?.operatorLabel;
            const hasReplacedOperator = ogOperatorLabel && ogOperatorLabel !== operatorLabel;

            return (
              <div key={`card-${restriction?.value}`} className={styles.formCards_card}>
                <div className={styles.cardHeader}>
                  <Heading type="h4">{restriction?.label}</Heading>
                  <Icon name="chevron-right" color="neutral-70" />
                  <div className={styles.cardHeader_conditions}>
                    <Heading
                      className={cx(
                        styles.conditionOperator,
                        restriction?.ogOperatorLabel &&
                          restriction?.ogOperatorLabel !== restriction?.operatorLabel &&
                          styles.conditionOperator__replaced,
                      )}
                      type="h4"
                    >
                      {ogOperatorLabel || operatorLabel}
                    </Heading>
                    {hasReplacedOperator && (
                      <>
                        <Icon name="repeat" color="neutral-70" size="sm" />

                        <Heading className={styles.conditionOperator} type="h4">
                          {operatorLabel}
                        </Heading>
                      </>
                    )}
                  </div>

                  <RoundButton
                    className={styles.cardHeader_removeBtn}
                    icon="trash"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      handleRemoveGroupRestriction({
                        attribute: restriction?.value,
                      });
                    }}
                  />
                </div>

                <div className={styles.cardTags}>
                  <AnimatePresence>
                    {restriction?.self?.values?.map(value => {
                      const valueData = getRestrictionValue(value);

                      return (
                        <motion.div
                          key={`value-${value.id}`}
                          layout
                          initial={{ opacity: 0, scale: 0.5 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.5 }}
                          transition={{ duration: 0.35, type: "spring", bounce: 0.35 }}
                        >
                          <Tag
                            icon="close"
                            onIconClick={() => {
                              handleRemoveGroupRestrictionValue({
                                attribute: restriction?.value,
                                valueId: value?.id,
                              });
                            }}
                          >
                            {valueData?.code}
                            {valueData?.code && valueData?.name ? ` | ` : ""}
                            {valueData?.name}
                          </Tag>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default GroupRestrictions;
